import { useCallback, useMemo } from 'react';
import type { AxiosResponse } from 'axios';

import { microServicesRoutes } from '@constants';
import { contactsApi } from '@lib/apiClients';
import { logError } from '@lib/logger/logError';
import { logInfo } from '@lib/logger/logInfo';
import { extractErrorMessage } from '@lib/logger/extractErrorMessage';

import type { ApiResponse, CreateContactListDto } from '@features/contacts/types/contacts.types';
import type {
  SdrCallSessionSummary,
  SessionContactItem,
  SessionContactsFilterDto,
  SessionContactStatus,
} from '@features/wizard/types/wizard.types';

/** Maps the chip status union to the backend boolean filter payload */
const buildFilterPayload = (
  callSessionIds: string[],
  statuses: SessionContactStatus[],
): SessionContactsFilterDto => ({
  callSessionIds,
  flagged: statuses.includes('fuegoLead'),
  no_answer: statuses.includes('noAnswer'),
  voicemail: statuses.includes('voicemail'),
  cut_off: statuses.includes('cutOff'),
  transfer_error: statuses.includes('transferError'),
});

export interface AddContactsToListPayload {
  contactListId: string;
  organizationId: string;
  contacts: Array<Omit<SessionContactItem, 'id'>>;
}

export interface UseSessionsApiReturn {
  /** GET /call-sessions/recycle-contacts/:sdrId/:orgId — lists past sessions' contact lists for an SDR */
  listSdrSessionsAsync: (sdrId: string, orgId: string, title?: string) => Promise<ApiResponse<SdrCallSessionSummary[]>>;

  /** POST /contacts/v2/recycle/call-session-ids — fetches contacts across N sessions filtered by status */
  fetchSessionContactsAsync: (
    callSessionIds: string[],
    statuses: SessionContactStatus[],
  ) => Promise<ApiResponse<SessionContactItem[]>>;

  /** POST /contact/lists — persists the new filtered list once the user clicks Next */
  createContactListAsync: (
    payload: CreateContactListDto,
  ) => Promise<ApiResponse<{ id: string; title: string }>>;

  /** POST /contacts/:contactListId?orgId=... — adds a chunk of contacts to a list */
  addContactsToListAsync: (
    payload: AddContactsToListPayload,
  ) => Promise<ApiResponse<{ count: number }>>;
}

export default function useSessionsApi (): UseSessionsApiReturn {
  const handleError = useCallback(<T, >(
    message: string,
    error: unknown,
    source: string,
    context: Record<string, unknown>,
  ): ApiResponse<T> => {
    const errorMessage = extractErrorMessage(error);
    void logError(message, source, { ...context, error: errorMessage, rawError: error }, false);
    return { success: false, error: { message, raw: errorMessage } };
  }, []);

  const listSdrSessionsAsync = useCallback(async (
    sdrId: string,
    orgId: string,
    title?: string,
  ): Promise<ApiResponse<SdrCallSessionSummary[]>> => {
    const source = 'Sessions.listSdrSessionsAsync';
    try {
      const baseUrl = `${microServicesRoutes.contacts.get.callSession.getRecycleContactsLists}/${sdrId}/${orgId}`;
      const requestUrl = title !== undefined && title.trim() !== ''
        ? `${baseUrl}?title=${encodeURIComponent(title)}`
        : baseUrl;
      const response: AxiosResponse<SdrCallSessionSummary[]> = await contactsApi.get(requestUrl);
      const sessions = response.data ?? [];
      const reversedSessions: SdrCallSessionSummary[] = [...sessions].reverse();
      void logInfo('Fetched SDR sessions.', source, { sdrId, orgId, total: sessions.length });
      return { success: true, data: reversedSessions };
    } catch (error) {
      return handleError<SdrCallSessionSummary[]>(
        'Error fetching SDR sessions.',
        error,
        source,
        { sdrId, orgId },
      );
    }
  }, [handleError]);

  const fetchSessionContactsAsync = useCallback(async (
    callSessionIds: string[],
    statuses: SessionContactStatus[],
  ): Promise<ApiResponse<SessionContactItem[]>> => {
    const source = 'Sessions.fetchSessionContactsAsync';
    if (callSessionIds.length === 0) {
      return { success: true, data: [] };
    }
    try {
      const requestUrl = microServicesRoutes.contacts.post.contact.recycleByCallSessionIds;
      const payload = buildFilterPayload(callSessionIds, statuses);
      const response: AxiosResponse<SessionContactItem[]> = await contactsApi.post(requestUrl, payload);
      const contacts = response.data ?? [];
      void logInfo('Fetched session contacts.', source, {
        sessionsCount: callSessionIds.length,
        statuses,
        contactsCount: contacts.length,
      });
      return { success: true, data: contacts };
    } catch (error) {
      return handleError<SessionContactItem[]>(
        'Error fetching session contacts.',
        error,
        source,
        { sessionsCount: callSessionIds.length, statuses },
      );
    }
  }, [handleError]);

  const createContactListAsync = useCallback(async (
    payload: CreateContactListDto,
  ): Promise<ApiResponse<{ id: string; title: string }>> => {
    const source = 'Sessions.createContactListAsync';
    try {
      const requestUrl = microServicesRoutes.contacts.post.contactList.create;
      const response: AxiosResponse<{ id: string; title: string }> = await contactsApi.post(requestUrl, payload);
      void logInfo('Created contact list from sessions.', source, {
        organizationId: payload.organizationId,
        title: payload.title,
        contactListId: response.data?.id,
      });
      return { success: true, data: response.data };
    } catch (error) {
      return handleError<{ id: string; title: string }>(
        'Error creating contact list from sessions.',
        error,
        source,
        { organizationId: payload.organizationId, title: payload.title },
      );
    }
  }, [handleError]);

  const addContactsToListAsync = useCallback(async (
    payload: AddContactsToListPayload,
  ): Promise<ApiResponse<{ count: number }>> => {
    const source = 'Sessions.addContactsToListAsync';
    try {
      const requestUrl = `${microServicesRoutes.contacts.post.contact.uploadCsvToContactList}/${payload.contactListId}?orgId=${payload.organizationId}`;
      const body = payload.contacts.map((contact) => ({
        ...contact,
        contactListId: payload.contactListId,
      }));
      const response: AxiosResponse<{ count: number }> = await contactsApi.post(requestUrl, body);
      void logInfo('Added contacts to list.', source, {
        contactListId: payload.contactListId,
        contactsCount: payload.contacts.length,
      });
      return { success: true, data: response.data ?? { count: payload.contacts.length } };
    } catch (error) {
      return handleError<{ count: number }>(
        'Error adding contacts to list.',
        error,
        source,
        { contactListId: payload.contactListId, contactsCount: payload.contacts.length },
      );
    }
  }, [handleError]);

  return useMemo(() => ({
    listSdrSessionsAsync,
    fetchSessionContactsAsync,
    createContactListAsync,
    addContactsToListAsync,
  }), [listSdrSessionsAsync, fetchSessionContactsAsync, createContactListAsync, addContactsToListAsync]);
}
